(() => {
  if (customElements.get('crestlane-market')) return;
  class CrestlaneMarket extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abort = new AbortController();
      this.currency = 'SGD';
      this.days = 30;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.dataset.currency) this.currency = button.dataset.currency;
        if (button.dataset.days) this.days = Number(button.dataset.days);
        if (button.hasAttribute('data-retry')) { this.fetchData(); return; }
        if (this.data) this.render();
      }, { signal: this.abort.signal });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-printing]')) this.render();
      }, { signal: this.abort.signal });
      if (this.data) this.render();
      else if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect(); this.fetchData();
          }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      } else this.fetchData();
    }
    disconnectedCallback() {
      this.connected = false;
      this.observer?.disconnect();
      this.abort?.abort();
      this.fetchAbort?.abort();
    }
    text(selector, text) { this.querySelector(selector).textContent = text; }
    value(value) {
      if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
      const rate = this.currency === 'SGD' ? this.data.fx?.rate : 1;
      if (!rate) return '—';
      return new Intl.NumberFormat('en-SG', { style: 'currency', currency: this.currency, currencyDisplay: 'narrowSymbol' }).format(value * rate);
    }
    date(day) {
      return new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' }).format(new Date(day.length === 10 ? day + 'T00:00:00+08:00' : day));
    }
    async fetchData() {
      this.fetchAbort?.abort();
      const controller = new AbortController(); this.fetchAbort = controller;
      const timeout = setTimeout(() => controller.abort(), 25000);
      this.text('[data-status]', this.labels.loading);
      this.querySelector('[data-retry]').hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.endpoint);
        url.searchParams.set('product', this.dataset.product);
        url.searchParams.set('set', this.dataset.set);
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (data.productId !== Number(this.dataset.product) || !Array.isArray(data.printings) || !data.printings.length) throw new Error('invalid');
        if (!this.isConnected || controller !== this.fetchAbort) return;
        this.data = data;
        const select = this.querySelector('[data-printing]');
        select.replaceChildren(...data.printings.map((p, i) => {
          const option = document.createElement('option');
          option.textContent = p.name; option.value = String(i); return option;
        }));
        select.closest('[data-printing-wrap]').hidden = data.printings.length < 2;
        if (!data.fx?.rate) this.currency = 'USD';
        this.render();
      } catch {
        if (!this.isConnected || controller !== this.fetchAbort) return;
        this.text('[data-status]', this.labels.unavailable);
        this.querySelector('[data-retry]').hidden = false;
      } finally {
        clearTimeout(timeout);
        if (controller === this.fetchAbort) this.setAttribute('aria-busy', 'false');
      }
    }
    render() {
      const printing = this.data.printings[Number(this.querySelector('[data-printing]').value) || 0];
      const cutoff = Date.now() - this.days * 86400000;
      const rows = printing.history.filter((row) => new Date(row.date + 'T00:00:00+08:00').getTime() >= cutoff);
      const priced = rows.filter((row) => typeof row.marketUSD === 'number' && Number.isFinite(row.marketUSD));
      this.querySelectorAll('[data-currency]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.currency === this.currency));
        button.disabled = button.dataset.currency === 'SGD' && !this.data.fx?.rate;
      });
      this.querySelectorAll('[data-days]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.days) === this.days)));
      this.querySelectorAll('[data-unit]').forEach((node) => { node.textContent = this.currency; });
      this.text('[data-market]', this.value(printing.marketUSD));
      this.text('[data-median]', this.value(printing.medianUSD));
      this.text('[data-low]', priced.length ? this.value(Math.min(...priced.map((p) => p.marketUSD))) : '—');
      this.text('[data-high]', priced.length ? this.value(Math.max(...priced.map((p) => p.marketUSD))) : '—');
      const first = priced[0]?.marketUSD, last = priced.at(-1)?.marketUSD;
      const change = priced.length >= 2 && first > 0 ? (last / first - 1) * 100 : null;
      this.text('[data-change]', change === null ? '—' : (change > 0 ? '+' : '') + change.toFixed(2) + '%');
      this.text('[data-updated]', this.labels.updated + ' ' + this.date(printing.updatedAt));
      this.text('[data-fx]', this.data.fx?.rate ? 'US$1 = S$' + this.data.fx.rate.toFixed(4) + ' · ' + this.data.fx.source + ' · ' + this.date(this.data.fx.date) : this.labels.no_fx);
      this.text('[data-status]', this.data.stale ? this.labels.stale : this.labels.reference);
      const firstObserved = printing.history[0]?.date;
      this.text('[data-observed]', firstObserved ? this.labels.started + ' ' + this.date(firstObserved) + '.' : this.labels.no_history);
      this.querySelector('[data-table-wrap]').hidden = !rows.length;
      this.querySelector('[data-tbody]').replaceChildren(...rows.slice().reverse().map((row) => {
        const tr = document.createElement('tr');
        [this.date(row.date), this.value(row.marketUSD), this.value(row.medianUSD)].forEach((value) => { const td = document.createElement('td'); td.textContent = value; tr.append(td); });
        return tr;
      }));
    }
  }
  customElements.define('crestlane-market', CrestlaneMarket);
})();

